<script>
import {ElMessage, ElNotification} from "element-plus";
import {Download, InfoFilled, Loading, Lock, Refresh, Search, StarFilled, Unlock, Upload} from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import ExportParamModal from "@/views/export-hub/ExportParamModal.vue";
import {visibleParams, downloadExport, extractErrorMessage} from "@/views/export-hub/exportHubApi";

const RECENT_KEY = "export_hub_recent_ids";

const GROUP_LABELS = {
  tashabbuslar: "Tashabbuslar",
  dashboard: "Dashboard",
  moliya: "Moliya",
  mahalla: "Mahalla",
  deputatlar: "Deputatlar",
  vazirlik: "Vazirlik",
  xodimlar: "Xodimlar",
  maktabim: "Maktabim",
  boshqa: "Boshqa",
};

// Categorical accent per group — kept distinct from the app's own
// primary/info tokens (#007791 / #424E79), which are reserved for the
// export/import action colors below.
const GROUP_COLORS = {
  tashabbuslar: "37, 99, 235", // blue
  dashboard: "99, 102, 241", // indigo
  moliya: "217, 119, 6", // amber
  mahalla: "22, 163, 74", // green
  deputatlar: "124, 58, 237", // violet
  vazirlik: "8, 145, 178", // cyan
  xodimlar: "219, 39, 119", // rose
  maktabim: "234, 88, 12", // orange
  boshqa: "100, 116, 139", // slate
};
const DEFAULT_GROUP_COLOR = "100, 116, 139";

// 09-export-hub-roles-fix.md: backend authorization is a role HIERARCHY, not a
// flat allowlist — admin passes every gate regardless of what `roles` lists, so
// `roles` is informational only and must never drive disable/hide logic. The
// only legitimate reason a card can't work for the current (admin) token is a
// handler-specific quirk called out in the catalog's own `note` text (e.g. a
// different auth scheme, or an id lookup that only resolves for non-admins).
const DISABLE_NOTE_PATTERN = /ishlamaydi|ishlamas/i;

export default {
  name: "ExportHub",
  components: {MfEmpty, ExportParamModal, Download, Upload, Search, StarFilled, Lock, Unlock, Refresh, InfoFilled, Loading},
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: "",
      items: [],
      search: "",
      activeKind: "export",
      activeGroup: "",
      actionLoadingId: null,
      boardsOptions: [],
      boardsLoading: false,
      boardsLoaded: false,
      globalBoardId: null,
      recentIds: [],
      modalVisible: false,
      modalItem: null,
    };
  },
  computed: {
    kindItems() {
      return this.items.filter((i) => i.kind === this.activeKind);
    },
    groupCounts() {
      const counts = {};
      this.kindItems.forEach((i) => {
        counts[i.group] = (counts[i.group] || 0) + 1;
      });
      return counts;
    },
    groupList() {
      return Object.keys(this.groupCounts)
          .map((g) => ({value: g, label: GROUP_LABELS[g] || g, count: this.groupCounts[g]}))
          .sort((a, b) => a.label.localeCompare(b.label, "uz"));
    },
    filteredItems() {
      let list = this.kindItems;
      if (this.activeGroup) {
        list = list.filter((i) => i.group === this.activeGroup);
      }
      const q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(
            (i) =>
                (i.title || "").toLowerCase().includes(q) ||
                (i.description || "").toLowerCase().includes(q) ||
                (i.group || "").toLowerCase().includes(q) ||
                (i.id || "").toLowerCase().includes(q)
        );
      }
      return list;
    },
    recentItems() {
      return this.recentIds
          .map((id) => this.items.find((i) => i.id === id))
          .filter((i) => i && i.kind === this.activeKind);
    },
    hasActiveFilters() {
      return !!this.search.trim() || !!this.activeGroup;
    },
  },
  watch: {
    activeKind() {
      this.activeGroup = "";
    },
  },
  methods: {
    groupLabel(g) {
      return GROUP_LABELS[g] || g;
    },
    groupColor(g) {
      return GROUP_COLORS[g] || DEFAULT_GROUP_COLOR;
    },
    isDisabled(item) {
      return !!(item.note && DISABLE_NOTE_PATTERN.test(item.note));
    },
    disabledReason(item) {
      return item.note || "";
    },
    hasParams(item) {
      return visibleParams(item).length > 0;
    },
    fetchCatalog() {
      this.loading = true;
      this.hasError = false;
      this.errorMessage = "";
      this.$http
          .get("api/v1/admin/exports")
          .then(({data}) => {
            this.items = data?.items || [];
          })
          .catch(({response}) => {
            this.hasError = true;
            this.errorMessage =
                response?.data?.message || "Excel markazi katalogini yuklashda xatolik yuz berdi.";
            ElNotification.error({title: "Xatolik", message: this.errorMessage});
          })
          .finally(() => {
            this.loading = false;
          });
    },
    ensureBoardsLoaded() {
      if (this.boardsLoaded || this.boardsLoading) return;
      this.boardsLoading = true;
      this.$http
          .get("api/v1/active-boards")
          .then(({data}) => {
            const list = Array.isArray(data) ? data : data?.boards || data?.items || [];
            this.boardsOptions = list.map((b) => ({
              value: b.id,
              label: `${b.title} (${b.year}, ${b.season}-mavsum)`,
            }));
            this.boardsLoaded = true;
          })
          .catch(() => {
            ElMessage.warning("Mavsumlar ro'yxatini yuklab bo'lmadi.");
          })
          .finally(() => {
            this.boardsLoading = false;
          });
    },
    pushRecent(id) {
      const next = [id, ...this.recentIds.filter((x) => x !== id)].slice(0, 5);
      this.recentIds = next;
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch (e) {
        // localStorage unavailable — recents just won't persist
      }
    },
    onCardClick(item) {
      if (this.isDisabled(item) || this.actionLoadingId) return;
      if (item.kind === "export" && !this.hasParams(item)) {
        this.quickDownload(item);
      } else {
        this.modalItem = item;
        this.modalVisible = true;
      }
    },
    async quickDownload(item) {
      this.actionLoadingId = item.id;
      try {
        await downloadExport(this.$http, item, {});
        ElMessage.success("Fayl yuklab olindi");
        this.pushRecent(item.id);
      } catch (error) {
        const message = await extractErrorMessage(error, "Faylni yuklab olishda xatolik yuz berdi.");
        ElNotification.error({title: `"${item.title}" — xatolik`, message});
      } finally {
        this.actionLoadingId = null;
      }
    },
    onModalSuccess(item) {
      this.pushRecent(item.id);
    },
    resetFilters() {
      this.search = "";
      this.activeGroup = "";
    },
  },
  mounted() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      this.recentIds = raw ? JSON.parse(raw) : [];
    } catch (e) {
      this.recentIds = [];
    }
    this.fetchCatalog();
    this.ensureBoardsLoaded();
  },
};
</script>

<template>
  <div class="export-hub">
    <div class="header">
      <div>
        <h2 class="page-title">Excel markazi</h2>
        <p class="page-subtitle">
          Tizimdagi barcha Excel yuklab olish va yuklash imkoniyatlari bitta joyda
        </p>
      </div>
    </div>

    <div v-if="hasError" class="state-block">
      <el-result icon="error" title="Katalogni yuklab bo'lmadi" :sub-title="errorMessage">
        <template #extra>
          <el-button type="primary" @click="fetchCatalog">
            <el-icon style="margin-right: 4px"><Refresh/></el-icon>
            Qayta urinish
          </el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="loading" class="state-block">
      <el-skeleton :rows="2" animated style="margin-bottom: 16px"/>
      <div class="cards-grid">
        <el-skeleton v-for="n in 6" :key="n" animated class="card-skeleton">
          <template #template>
            <el-skeleton-item variant="h3" style="width: 60%"/>
            <el-skeleton-item variant="text" style="margin-top: 12px"/>
            <el-skeleton-item variant="text" style="width: 80%"/>
            <el-skeleton-item variant="button" style="margin-top: 16px; width: 140px"/>
          </template>
        </el-skeleton>
      </div>
    </div>

    <template v-else>
      <div class="toolbar">
        <el-radio-group v-model="activeKind" class="kind-switch">
          <el-radio-button label="export">
            <el-icon><Download/></el-icon>
            <span>Yuklab olish</span>
          </el-radio-button>
          <el-radio-button label="import" class="kind-switch__import">
            <el-icon><Upload/></el-icon>
            <span>Yuklash</span>
          </el-radio-button>
        </el-radio-group>

        <el-select
            v-model="globalBoardId"
            placeholder="Mavsum (global)"
            filterable
            clearable
            :loading="boardsLoading"
            class="global-board-select"
            @focus="ensureBoardsLoaded"
        >
          <el-option v-for="b in boardsOptions" :key="b.value" :label="b.label" :value="b.value"/>
        </el-select>

        <el-input
            v-model="search"
            placeholder="Nomi, tavsifi yoki guruhi bo'yicha qidirish"
            clearable
            class="search-input"
        >
          <template #prefix>
            <el-icon><Search/></el-icon>
          </template>
        </el-input>
      </div>

      <div class="layout">
        <aside class="groups-sidebar">
          <div
              class="group-item"
              :class="{active: !activeGroup}"
              @click="activeGroup = ''"
          >
            <span class="group-item__label">
              <span class="group-dot group-dot--all"/>
              Barchasi
            </span>
            <span class="group-count">{{ kindItems.length }}</span>
          </div>
          <div
              v-for="g in groupList"
              :key="g.value"
              class="group-item"
              :class="{active: activeGroup === g.value}"
              @click="activeGroup = g.value"
          >
            <span class="group-item__label">
              <span class="group-dot" :style="{background: `rgb(${groupColor(g.value)})`}"/>
              {{ g.label }}
            </span>
            <span class="group-count">{{ g.count }}</span>
          </div>
        </aside>

        <div class="content">
          <div v-if="recentItems.length && !hasActiveFilters" class="recent-section">
            <h4 class="recent-title">
              <el-icon class="recent-title__icon"><StarFilled/></el-icon>
              Tez-tez ishlatiladiganlar
            </h4>
            <div class="recent-chips">
              <span
                  v-for="item in recentItems"
                  :key="item.id"
                  class="recent-chip"
                  @click="activeGroup = item.group; search = item.title"
              >
                {{ item.title }}
              </span>
            </div>
          </div>

          <p v-if="filteredItems.length" class="results-meta">
            <b>{{ filteredItems.length }}</b> ta natija topildi
          </p>

          <div v-if="!filteredItems.length" class="state-block">
            <MfEmpty
                title="Hech narsa topilmadi"
                description="Qidiruv so'zini yoki guruh filtrini o'zgartirib ko'ring"
            />
            <div v-if="hasActiveFilters" class="empty-actions">
              <el-button @click="resetFilters">Filtrlarni tozalash</el-button>
            </div>
          </div>

          <div v-else class="cards-grid">
            <el-tooltip
                v-for="item in filteredItems"
                :key="item.id"
                :disabled="!isDisabled(item)"
                :content="disabledReason(item)"
                placement="top"
            >
              <div
                  class="export-card"
                  :class="{'export-card--disabled': isDisabled(item)}"
                  :style="{'--accent': groupColor(item.group)}"
                  @click="onCardClick(item)"
              >
                <div v-if="actionLoadingId === item.id" class="export-card__overlay">
                  <el-icon class="is-loading"><Loading/></el-icon>
                </div>

                <div class="export-card__head">
                  <span class="export-card__title">{{ item.title }}</span>
                  <span class="export-card__badge">{{ groupLabel(item.group) }}</span>
                </div>

                <p class="export-card__description">{{ item.description }}</p>

                <div class="export-card__footer">
                  <span class="export-card__roles">
                    <el-icon v-if="item.roles && item.roles.length"><Lock/></el-icon>
                    <el-icon v-else><Unlock/></el-icon>
                    <span v-if="item.roles && item.roles.length">{{ item.roles.join(", ") }}</span>
                    <span v-else>Ochiq</span>
                  </span>

                  <span class="export-card__actions">
                    <el-tooltip v-if="item.note" :content="item.note" placement="top">
                      <el-icon class="export-card__note"><InfoFilled/></el-icon>
                    </el-tooltip>
                    <el-icon v-if="item.kind === 'export'" class="export-card__kind"><Download/></el-icon>
                    <el-icon v-else class="export-card__kind export-card__kind--import"><Upload/></el-icon>
                  </span>
                </div>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>
    </template>

    <ExportParamModal
        v-model:visible="modalVisible"
        :item="modalItem"
        :global-board-id="globalBoardId"
        :boards-options="boardsOptions"
        :boards-loading="boardsLoading"
        @ensure-boards="ensureBoardsLoaded"
        @success="onModalSuccess"
    />
  </div>
</template>

<style scoped lang="scss">
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;

  .page-title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    color: #0a194e;
  }

  .page-subtitle {
    margin: 4px 0 0;
    color: #777777;
    font-size: 13px;
  }
}

.state-block {
  padding: 24px 0;
}

.empty-actions {
  display: flex;
  justify-content: center;
  margin-top: -12px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 12px 16px;
  background: #fafbfd;
  border: 1px solid rgba(66, 78, 121, 0.08);
  border-radius: 10px;
}

.kind-switch {
  :deep(.el-radio-button__inner) {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // The import action reuses the app's own --el-color-info token (already
  // defined in custom.css) so export/import stay visually distinct without
  // introducing a new brand color. Element Plus reads the checked-state
  // color from these component-level CSS vars, so setting them on the
  // radio-button's own root is enough — no need to fight its default
  // .is-active selector specificity.
  &__import {
    --el-radio-button-checked-bg-color: var(--el-color-info);
    --el-radio-button-checked-border-color: var(--el-color-info);
    --el-radio-button-checked-text-color: #fff;
  }
}

.global-board-select {
  width: 220px;
}

.search-input {
  max-width: 320px;
  flex: 1 1 240px;
}

.layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.groups-sidebar {
  flex: 0 0 220px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: sticky;
  top: 12px;
}

.group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  border-left: 3px solid transparent;
  transition: background-color 0.12s ease, border-color 0.12s ease;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &:hover {
    background: #f5f7fb;
  }

  &.active {
    background: rgba(0, 119, 145, 0.08);
    border-left-color: var(--el-color-primary);
    color: var(--el-color-primary);
    font-weight: 600;
  }

  .group-count {
    color: #999;
    font-size: 12px;
  }
}

.group-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--all {
    background: linear-gradient(135deg, var(--el-color-primary), var(--el-color-info));
  }
}

.content {
  flex: 1;
  min-width: 0;
}

.results-meta {
  margin: 0 0 12px;
  font-size: 12px;
  color: #424e79;

  b {
    color: #0a194e;
  }
}

.recent-section {
  margin-bottom: 18px;

  .recent-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: 13px;
    color: #b45309;
    font-weight: 600;

    &__icon {
      font-size: 14px;
    }
  }

  .recent-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .recent-chip {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    font-size: 12px;
    border-radius: 999px;
    background: rgba(217, 119, 6, 0.08);
    border: 1px solid rgba(217, 119, 6, 0.25);
    color: #b45309;
    cursor: pointer;
    transition: background-color 0.12s ease;

    &:hover {
      background: rgba(217, 119, 6, 0.16);
    }
  }
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  align-items: stretch;
}

.card-skeleton {
  border: 1px solid rgba(137, 141, 141, 0.2);
  border-radius: 8px;
  padding: 16px;
}

.export-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 148px;
  border: 1px solid rgba(137, 141, 141, 0.16);
  border-left: 4px solid rgb(var(--accent, 100, 116, 139));
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(10, 25, 78, 0.04);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    box-shadow: 0 10px 22px rgba(10, 25, 78, 0.09);
    transform: translateY(-2px);
    border-color: rgba(var(--accent, 100, 116, 139), 0.5);
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.55;
    filter: grayscale(0.6);

    &:hover {
      box-shadow: 0 2px 6px rgba(10, 25, 78, 0.04);
      transform: none;
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 7px;
    font-size: 22px;
    color: var(--el-color-primary);
    z-index: 1;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-weight: 600;
    color: #0a194e;
    font-size: 14px;
  }

  &__badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(var(--accent, 100, 116, 139), 0.1);
    color: rgb(var(--accent, 100, 116, 139));
    white-space: nowrap;
  }

  &__description {
    margin: 8px 0 0;
    font-size: 12px;
    color: #777;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__footer {
    margin-top: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__roles {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #999;

    .el-icon {
      font-size: 12px;
    }
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__note {
    color: #e6a23c;
    font-size: 14px;
  }

  &__kind {
    font-size: 14px;
    color: var(--el-color-primary);

    &--import {
      color: var(--el-color-info);
    }
  }
}

@media (max-width: 768px) {
  .layout {
    flex-direction: column;
  }

  .groups-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
    position: static;
    width: 100%;
  }
}
</style>
